import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';
import config from '../../config';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { Modal, Button } from 'react-bootstrap';

const PricingPlanMultipleColor = ({ onSubscribe }) => {
    const { user, auth } = useAuth();
    const [plans, setPlans] = useState([]);
    const [currentSub, setCurrentSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('monthly'); // 'monthly' or 'yearly'
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Plans
                const plansRes = await fetch(`${config.API_BASE_URL}/api/plans`);
                const plansData = await plansRes.json();
                
                if (plansRes.ok) {
                    setPlans(plansData);
                } else {
                    toast.error('Failed to load plans');
                }

                // Fetch Current Subscription if logged in
                if (user) {
                    const subRes = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
                    if (subRes.ok) {
                        const subData = await subRes.json();
                        setCurrentSub(subData.subscription);
                    }
                }

            } catch (error) {
                console.error('Error loading data:', error);
                toast.error('Failed to load pricing data.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, auth]);

    const handleSubscribeClick = (plan) => {
        // [DEBUG] Log plan selection
        console.log('[DEBUG] handleSubscribeClick:', plan);

        if (!user) {
            toast.error('Please log in to subscribe.');
            // Ideally redirect to login
            return;
        }

        if (currentSub) {
            if (currentSub.plan_id === plan.id) {
                return; // Already on this plan
            }
            // Open Modal for Upgrade/Downgrade
            console.log('[DEBUG] Opening modal for plan:', plan);
            setSelectedPlan(plan);
            setShowModal(true);
        } else {
            // New Subscription
            initiateCheckout(plan);
        }
    };

    const initiateCheckout = async (plan) => {
        setProcessing(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/create-checkout-session`, {
                method: 'POST',
                body: JSON.stringify({
                    planId: plan.id,
                    userId: user.id
                }),
            });

            const data = await response.json();

            if (data.url) {
                window.location.href = data.url;
            } else if (data.id) {
                 // Fallback if URL is missing but ID is present (shouldn't happen with updated controller)
                 toast.error('Payment session created but redirect URL is missing.');
            } else {
                toast.error(data.error || 'Payment initialization failed.');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('An error occurred.');
        } finally {
            setProcessing(false);
        }
    };

    const confirmChange = async () => {
        console.log('[DEBUG] confirmChange triggered. Selected Plan:', selectedPlan);
        
        if (!selectedPlan || !currentSub) {
             console.warn('[WARN] Missing selectedPlan or currentSub:', { selectedPlan, currentSub });
             return;
        }
        setProcessing(true);

        try {
            const payload = {
                subscriptionId: currentSub.id,
                newPriceId: selectedPlan.stripe_price_id
            };
            console.log('[DEBUG] Sending payload to update-subscription:', payload);

            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/update-subscription`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                 // Check for redirect URL (SCA / Payment required)
                 if (data.approvalUrl) {
                     console.log('[DEBUG] Payment required. Redirecting to:', data.approvalUrl);
                     toast.info('Redirecting to payment gateway...');
                     window.location.href = data.approvalUrl;
                     return;
                 }

                 // strict check for subscription status
                 const subStatus = data.subscription?.status;
                if (subStatus === 'active' || subStatus === 'trialing') {
                    toast.success(`Successfully switched to ${selectedPlan.name}!`);
                    setShowModal(false);
                    // Refresh subscription data
                    const subRes = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
                    if (subRes.ok) {
                        const subData = await subRes.json();
                        setCurrentSub(subData.subscription);
                    }
                } else {
                    console.warn('[WARN] Subscription updated but status is:', subStatus);
                    toast.warning(`Subscription updated but payment is ${subStatus}. Please check your payment method.`);
                    setShowModal(false);
                    // Still refresh to show current state
                    const subRes = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
                    if (subRes.ok) {
                        const subData = await subRes.json();
                        setCurrentSub(subData.subscription);
                    }
                }
            } else {
                toast.error(data.error || 'Failed to update subscription.');
            }
        } catch (error) {
            console.error('Update error:', error);
            toast.error('An error occurred.');
        } finally {
            setProcessing(false);
        }
    };

    const filteredPlans = plans.filter(plan => 
        (plan.duration_type || 'Monthly').toLowerCase() === activeTab.toLowerCase()
    );

    // Helper to parse features
    const getFeatures = (features) => {
        if (!features) return [];
        let parsedFeatures = features;
        
        if (typeof features === 'string') {
            try {
                parsedFeatures = JSON.parse(features);
            } catch (e) {
                return features.split(',').map(f => f.trim());
            }
        }

        // If it's an array (old format or list of strings), return it
        if (Array.isArray(parsedFeatures)) return parsedFeatures;

        // If it's an object (new structured format), convert to displayable strings
        if (typeof parsedFeatures === 'object') {
            const displayList = [];
            if (parsedFeatures.galleryLimit) displayList.push(`Up to ${parsedFeatures.galleryLimit} Gallery Images`);
            if (parsedFeatures.blogs) displayList.push(`Up to ${parsedFeatures.blogs} Blog Posts`);
            if (parsedFeatures.rateOverview) displayList.push('Rate Overview & Analytics');
            if (parsedFeatures.premiumColor) displayList.push('Premium Profile Colors');
            // Add other keys if necessary
            return displayList;
        }

        return [];
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="mb-0 text-lg">Pricing Plans</h6>
            </div>
            <div className="card-body p-40">
                
                {/* Duration Toggle */}
                <div className="d-flex justify-content-center mb-40">
                    <div className="bg-neutral-100 p-1 radius-8 d-inline-flex">
                        <button 
                            className={`btn btn-sm radius-8 px-24 py-10 transition-2 ${activeTab === 'monthly' ? 'bg-primary-600 text-white' : 'text-secondary-light hover-bg-neutral-200'}`}
                            onClick={() => setActiveTab('monthly')}
                        >
                            Monthly
                        </button>
                        <button 
                            className={`btn btn-sm radius-8 px-24 py-10 transition-2 ${activeTab === 'yearly' ? 'bg-primary-600 text-white' : 'text-secondary-light hover-bg-neutral-200'}`}
                            onClick={() => setActiveTab('yearly')}
                        >
                            Yearly
                        </button>
                    </div>
                </div>

                <div className="row gy-4 justify-content-center">
                    {filteredPlans.length === 0 ? (
                         <div className="text-center">
                             <p className="text-secondary-light">No plans available for this duration.</p>
                         </div>
                    ) : (
                        filteredPlans.map((plan, index) => {
                            const isCurrent = currentSub && currentSub.plan_id === plan.id;
                            const features = getFeatures(plan.features);

                            return (
                                <div key={plan.id} className="col-xxl-4 col-lg-6 col-md-6">
                                    <div className={`pricing-card h-100 p-32 radius-12 border transition-2 ${isCurrent ? 'border-primary-600 bg-primary-50' : 'border-neutral-200 hover-border-primary-600'}`}>
                                        <div className="mb-32">
                                            <span className="text-secondary-light text-md fw-medium mb-12 d-block">{plan.name}</span>
                                            <div className="d-flex align-items-end gap-1">
                                                <h2 className="mb-0 text-primary-600 fw-bold">AED {plan.price}</h2>
                                    <span className="text-secondary-light text-sm">/{plan.duration_type === 'yearly' ? 'year' : 'month'}</span>
                                            </div>
                                            {isCurrent && <span className="badge bg-primary-100 text-primary-600 mt-2">Current Plan</span>}
                                        </div>

                                        <ul className="d-flex flex-column gap-16 mb-32">
                                            {/* Static Limits Display */}
                                            <li className="d-flex align-items-center gap-2 text-secondary-light">
                                                <Icon icon="mdi:check-circle" className="text-primary-600 text-xl" />
                                                <span className="text-md fw-medium text-primary-600">Up to {plan.max_venues} Venues</span>
                                            </li>
                                            <li className="d-flex align-items-center gap-2 text-secondary-light">
                                                <Icon icon="mdi:check-circle" className="text-primary-600 text-xl" />
                                                <span className="text-md fw-medium text-primary-600">Up to {plan.max_features} Features/Venue</span>
                                            </li>

                                            {/* Dynamic Features */}
                                            {features.map((feature, i) => (
                                                <li key={i} className="d-flex align-items-center gap-2 text-secondary-light">
                                                    <Icon icon="mdi:check-circle" className="text-primary-600 text-xl" />
                                                    <span className="text-md">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <button 
                                            onClick={() => handleSubscribeClick(plan)}
                                            disabled={isCurrent || processing}
                                            className={`btn w-100 radius-8 py-12 ${isCurrent ? 'btn-neutral-200 text-neutral-400' : 'btn-primary-600'}`}
                                        >
                                            {isCurrent ? 'Current Plan' : (currentSub ? 'Switch Plan' : 'Subscribe Now')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Plan Change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to switch to <strong>{selectedPlan?.name}</strong>?</p>
                    <div className="alert alert-warning">
                        <small>
                            Note: Any price difference will be prorated and charged/credited immediately. 
                            Your new billing cycle may change.
                        </small>
                    </div>
                    <div className="mt-3">
                        <div>
                            <strong>New Price:</strong> AED {selectedPlan?.price}/{selectedPlan?.duration_type === 'yearly' ? 'yr' : 'mo'}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={processing}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmChange} disabled={processing}>
                        {processing ? 'Processing...' : 'Confirm Switch'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default PricingPlanMultipleColor;
